import { Injectable } from '@angular/core';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { ColDef, ColGroupDef, GridOptions, ValueGetterParams } from 'ag-grid-community';
import { ReviewPermissionCellRendererComponent } from './review-permission-cell-renderer.component';
import { ReviewPermissionsListViewData } from './review-permissions-list-view.model';

@Injectable()
export class ReviewPermissionsAgGridService {
  readonly gridOptions: GridOptions<ReviewPermissionsListViewData> = {
    suppressCellFocus: true,
    suppressContextMenu: true,
    groupDisplayType: 'groupRows',
    groupDefaultExpanded: -1, // Expand all groups by default
    groupMaintainOrder: false, // Allow sorting to reorder groups based on aggregated values
    initialGroupOrderComparator: (params) => {
      const programCodeA = params.nodeA.allLeafChildren?.[0]?.data?.syllabus?.program?.code ?? '';
      const programCodeB = params.nodeB.allLeafChildren?.[0]?.data?.syllabus?.program?.code ?? '';
      return programCodeA.localeCompare(programCodeB);
    },
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No review permissions found',
      body: 'No syllabuses with review permissions assigned',
    },
    // Header configuration - normal height for group headers, increased for column headers
    headerHeight: 80,
    groupHeaderHeight: 40,
    animateRows: true,
    defaultColDef: {
      suppressHeaderMenuButton: true,
      width: 150,
      minWidth: 120,
      suppressMovable: true,
      menuTabs: [],
      resizable: true,
      sortable: true,
      floatingFilter: false,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: false,
      cellStyle: { textAlign: 'left' },
      headerClass: 'text-left',
      checkboxSelection: false, // Disable checkboxes by default
      wrapHeaderText: true, // Enable header text wrapping
      autoHeaderHeight: true, // Auto adjust header height based on content
    },
    autoGroupColumnDef: {
      headerName: 'Program',
      width: 150,
      minWidth: 130,
      maxWidth: 220,
      pinned: 'left',
      sortable: true,
      cellStyle: { textAlign: 'left' },
      headerClass: 'text-left',
      checkboxSelection: false, // Disable checkboxes for group rows
    },
    suppressAggFuncInHeader: true,
    includeHiddenColumnsInQuickFilter: true,
    quickFilterText: '',
    groupAggFiltering: true,
    alwaysAggregateAtRootLevel: true,
    multiSortKey: 'ctrl',
    allowContextMenuWithControlKey: true,
    columnHoverHighlight: true, // Enable column/row highlighting on hover
    suppressRowClickSelection: true, // Prevent row selection on click
    suppressRowDeselection: true, // Prevent row deselection
    getRowStyle: (params) => {
      // Apply styles to group rows (program rows like "IBDP", "HSC")
      if (params.node.group) {
        const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

        if (isDarkMode) {
          return {
            fontWeight: '600', // Semi-bold instead of bold
            backgroundColor: 'rgb(31 41 55)', // Dark gray background for dark mode
            borderBottom: '1px solid rgb(55 65 81)', // Darker border for dark mode
            color: 'rgb(229 231 235)', // Light text for dark mode
          };
        } else {
          return {
            fontWeight: '600', // Semi-bold instead of bold
            backgroundColor: 'rgb(249 250 251)', // Light gray background for light mode
            borderBottom: '1px solid rgb(229 231 235)', // Gray border for light mode
            color: 'rgb(31 41 55)', // Dark text for light mode
          };
        }
      }
      return undefined;
    },
  };

  readonly columnDefs: Array<ColDef<ReviewPermissionsListViewData> | ColGroupDef<ReviewPermissionsListViewData>> = [
    // Group by program (first level only)
    {
      field: 'syllabus.program.code',
      hide: true,
      rowGroup: true,
      cellClass: 'font-medium text-gray-900 dark:text-gray-100',
    },
    // Syllabus as regular visible column
    {
      field: 'syllabus.name',
      headerName: 'Syllabus',
      width: 240,
      minWidth: 150,
      pinned: 'left',
      tooltipValueGetter: ({ data }) => data?.syllabus?.name ?? '',
      valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) => params.data?.syllabus?.name ?? '',
      cellStyle: { textAlign: 'left' },
      headerClass: 'text-left',
      checkboxSelection: false, // Disable checkboxes for this column
    },
    {
      headerName: 'Reference Question',
      wrapHeaderText: true,
      openByDefault: true,
      children: [
        {
          field: 'permissions.reference_question_review',
          headerName: 'Question Review',
          width: 160,
          minWidth: 130,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'reference_question_review',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.reference_question_review ?? false,
          headerTooltip: 'Reference Question: Question Review',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
        {
          field: 'permissions.reference_markscheme_review',
          headerName: 'Markscheme Review',
          width: 180,
          minWidth: 140,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'reference_markscheme_review',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.reference_markscheme_review ?? false,
          headerTooltip: 'Reference Question: Markscheme Review',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
        {
          field: 'permissions.reference_expert_markscheme_review',
          headerName: 'Expert Markscheme Review',
          width: 220,
          minWidth: 180,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'reference_expert_markscheme_review',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.reference_expert_markscheme_review ?? false,
          headerTooltip: 'Reference Question: Expert Markscheme Review',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
        {
          field: 'permissions.reference_image_review',
          headerName: 'Image Review',
          width: 150,
          minWidth: 120,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'reference_image_review',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.reference_image_review ?? false,
          headerTooltip: 'Reference Question: Image Review',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
        },
        {
          field: 'permissions.reference_expert_image_review',
          headerName: 'Expert Image Review',
          width: 190,
          minWidth: 150,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'reference_expert_image_review',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.reference_expert_image_review ?? false,
          headerTooltip: 'Reference Question: Expert Image Review',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
      ],
    },
    {
      headerName: 'Derivative Question',
      wrapHeaderText: true,
      openByDefault: true,
      children: [
        {
          field: 'permissions.derivative_prescreening',
          headerName: 'Prescreening',
          width: 120,
          minWidth: 100,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'derivative_prescreening',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.derivative_prescreening ?? false,
          headerTooltip: 'Derivative Question: Prescreening',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
        },
        {
          field: 'permissions.derivative_expert_prescreening',
          headerName: 'Expert Prescreening',
          width: 150,
          minWidth: 100,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'derivative_expert_prescreening',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.derivative_expert_prescreening ?? false,
          headerTooltip: 'Derivative Question: Expert Prescreening',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
        {
          field: 'permissions.derivative_image_review',
          headerName: 'Image Review',
          width: 120,
          minWidth: 100,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'derivative_image_review',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.derivative_image_review ?? false,
          headerTooltip: 'Derivative Question: Image Review',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
        {
          field: 'permissions.derivative_expert_image_review',
          headerName: 'Expert Image Review',
          width: 150,
          minWidth: 100,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'derivative_expert_image_review',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.derivative_expert_image_review ?? false,
          headerTooltip: 'Derivative Question: Expert Image Review',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
        {
          field: 'permissions.derivative_human_review',
          headerName: 'Human Review',
          width: 120,
          minWidth: 100,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'derivative_human_review',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.derivative_human_review ?? false,
          headerTooltip: 'Derivative Question: Human Review',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
        {
          field: 'permissions.derivative_tie_break',
          headerName: 'Tie Break',
          width: 100,
          minWidth: 100,
          cellRenderer: ReviewPermissionCellRendererComponent,
          cellRendererParams: {
            reviewMode: 'derivative_tie_break',
          },
          valueGetter: (params: ValueGetterParams<ReviewPermissionsListViewData>) =>
            params.data?.permissions?.derivative_tie_break ?? false,
          headerTooltip: 'Derivative Question: Tie Break',
          cellStyle: { textAlign: 'center' },
          headerClass: 'text-center',
          columnGroupShow: 'open',
        },
      ],
    },
  ];
}
